import React, { useState, useRef, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import cn from "../../../utils/classes"

interface PopoverProps {
  triggerElement: React.ReactNode
  scrollElementRef: React.MutableRefObject<HTMLDivElement | null>
  children: React.ReactNode
  elementPosition?: string
  styleOverrides?: string
  containerStyleOverrides?: string
  id?: string
  tabIndex?: number
  onKeyDown?: (event: React.KeyboardEvent) => void
  onClickCb?: () => Promise<void>
  [key: `data-${string}`]: string | undefined
}
export const Popover: React.FC<PopoverProps> = ({
  triggerElement,
  scrollElementRef,
  children,
  elementPosition,
  styleOverrides,
  containerStyleOverrides,
  id,
  tabIndex = 0,
  onKeyDown: externalOnKeyDown,
  onClickCb: externalOnClickCb,
  ...rest
}) => {
  const [position, setPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const [shouldShowPopover, setShouldShowPopover] = useState<boolean>(false)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLDivElement | null>(null)

  const updatePopoverPosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const tooltipWidth = popoverRef.current?.offsetWidth || 0
    const tooltipHeight = popoverRef.current?.offsetHeight || 0

    switch (elementPosition) {
      case "upload-practice-image":
      case "upload-provider-image":
        setPosition({
          top: rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2, //align middle of tooltip y-axis with the trigger element
          left: rect.right + window.scrollX + 15,
        })
        return
      case "left":
        setPosition({
          top: rect.top + window.scrollY - tooltipHeight / 2 + rect.height / 2,
          left: rect.left + window.scrollX - tooltipWidth - 15,
        })
        return
      case "right":
        setPosition({
          top: rect.top + window.scrollY - tooltipHeight / 2 + rect.height / 2,
          left: rect.right + window.scrollX + 15,
        })
        return
      case "top":
        setPosition({
          top: rect.top + window.scrollY - tooltipHeight - 10, // 10px above the trigger element
          left: rect.left + window.scrollX + rect.width / 2 - tooltipWidth / 2, // Center the tooltip horizontally
        })
        return
      case "bottom":
        setPosition({
          top: rect.bottom + window.scrollY + 10, // 10px below the trigger element
          left: rect.left + window.scrollX + rect.width / 2 - tooltipWidth / 2, // Center the tooltip horizontally
        })
        return
      case "bottom-left":
        setPosition({
          top: rect.bottom + window.scrollY + 10,
          left: rect.left + window.scrollX,
        })
        return
      default:
        setPosition({
          top: rect.bottom + window.scrollY + 5,
          left: rect.right + window.scrollX - tooltipWidth, // Right-align the tooltip
        })
    }
  }, [elementPosition])

  const handleClick = async () => {
    await externalOnClickCb?.()
    setShouldShowPopover((prev) => !prev)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setShouldShowPopover(false)
      }
    }

    const handleScroll = () => {
      if (shouldShowPopover) updatePopoverPosition()
    }

    const refElement = scrollElementRef?.current
    if (shouldShowPopover) {
      updatePopoverPosition()
      document.addEventListener("mousedown", handleClickOutside)
      window.addEventListener("scroll", handleScroll)
      window.addEventListener("resize", handleScroll)
      if (refElement) {
        refElement.addEventListener("scroll", handleScroll)
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleScroll)
      if (refElement) {
        refElement.removeEventListener("scroll", handleScroll)
      }
    }
  }, [shouldShowPopover, scrollElementRef, updatePopoverPosition])

  const hasVisibleWidth = popoverRef.current?.offsetWidth ?? 0 > 0
  return (
    <div className={containerStyleOverrides}>
      <div
        id={id}
        ref={triggerRef}
        className={cn("cursor-pointer", styleOverrides)}
        role="button"
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault()
            event.stopPropagation()
            handleClick()
          }
          externalOnKeyDown?.(event)
        }}
        tabIndex={tabIndex}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          handleClick()
        }}
        {...rest}
      >
        {triggerElement}
      </div>
      {createPortal(
        <div
          ref={popoverRef}
          style={{
            visibility:
              shouldShowPopover && hasVisibleWidth ? "visible" : "hidden",
            position: "absolute",
            top: position?.top,
            left: position?.left,
            zIndex: 1000,
          }}
        >
          {children}
        </div>,
        document.body,
      )}
    </div>
  )
}
